import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { supabase } from '@/lib/supabaseClient';

interface Plan {
  id: string;
  name: string;
  description: string | null;
  price_monthly: number;
  max_users: number;
  max_contracts: number;
  storage_mb: number;
  color: string;
  is_active: boolean;
}

interface Props {
  plan: Plan | null;
  onClose: () => void;
  onSaved: () => void;
}

const SW = { background: '#ffffff', color: '#0f172a' };

const DEFAULT_COLORS: Record<string, string> = {
  free: '#9ca3af',
  pro: '#f59e0b',
  enterprise: '#34d399',
};

const PRESET_COLORS = ['#9ca3af', '#f59e0b', '#34d399', '#3b82f6', '#8b5cf6', '#ec4899', '#ef4444', '#14b8a6'];

export default function PlanModal({ plan, onClose, onSaved }: Props) {
  const isEdit = !!plan;

  const [form, setForm] = useState({
    id: '',
    name: '',
    description: '',
    price_monthly: 0,
    max_users: 1,
    max_contracts: 10,
    storage_mb: 512,
    color: '#9ca3af',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plan) {
      setForm({
        id: plan.id,
        name: plan.name,
        description: plan.description ?? '',
        price_monthly: plan.price_monthly,
        max_users: plan.max_users,
        max_contracts: plan.max_contracts,
        storage_mb: plan.storage_mb,
        color: plan.color ?? DEFAULT_COLORS[plan.id] ?? '#9ca3af',
        is_active: plan.is_active,
      });
    }
  }, [plan]);

  const set = (key: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      Swal.fire({ icon: 'warning', title: 'Nombre requerido', ...SW }); return;
    }
    if (!form.id.trim()) {
      Swal.fire({ icon: 'warning', title: 'ID requerido', text: 'Ej: free, pro, enterprise', ...SW }); return;
    }

    setSaving(true);
    const payload = {
      id: form.id.trim().toLowerCase().replace(/\s+/g, '_'),
      name: form.name.trim(),
      description: form.description.trim() || null,
      price_monthly: Number(form.price_monthly),
      max_users: Number(form.max_users),
      max_contracts: Number(form.max_contracts),
      storage_mb: Number(form.storage_mb),
      color: form.color,
      is_active: form.is_active,
      updated_at: new Date().toISOString(),
    };

    let error;
    if (isEdit) {
      ({ error } = await supabase.from('saas_plans').update(payload).eq('id', payload.id));
    } else {
      ({ error } = await supabase.from('saas_plans').insert(payload));
    }

    setSaving(false);
    if (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return;
    }
    Swal.fire({
      icon: 'success',
      title: isEdit ? 'Plan actualizado' : 'Plan creado',
      timer: 1400,
      showConfirmButton: false,
      ...SW,
    });
    onSaved();
  };

  const storageLabel = (mb: number) => {
    if (mb >= 1024) return `${(mb / 1024).toFixed(0)} GB`;
    return `${mb} MB`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <i className="ri-price-tag-3-line text-lg"></i>
            </span>
            <div>
              <h2 className="text-slate-900 text-base font-semibold">{isEdit ? 'Editar Plan' : 'Nuevo Plan'}</h2>
              <p className="text-slate-400 text-xs">{isEdit ? `Editando "${plan?.name}"` : 'Crea un nuevo plan de suscripción'}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer transition-colors">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* ID + Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-600 text-xs font-semibold mb-1.5 uppercase tracking-wider">ID del Plan *</label>
              <input
                type="text"
                value={form.id}
                onChange={(e) => set('id', e.target.value)}
                disabled={isEdit}
                placeholder="free, pro, enterprise..."
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed font-mono"
              />
              {isEdit && <p className="text-slate-400 text-[10px] mt-1">El ID no se puede cambiar</p>}
            </div>
            <div>
              <label className="block text-slate-600 text-xs font-semibold mb-1.5 uppercase tracking-wider">Nombre *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Free, Pro, Enterprise..."
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-slate-600 text-xs font-semibold mb-1.5 uppercase tracking-wider">Descripción</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              placeholder="Descripción breve del plan..."
              className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Price */}
          <div>
            <label className="block text-slate-600 text-xs font-semibold mb-1.5 uppercase tracking-wider">Precio mensual (USD)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">$</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={form.price_monthly}
                onChange={(e) => set('price_monthly', e.target.value)}
                className="w-full pl-7 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {Number(form.price_monthly) === 0 && (
              <p className="text-emerald-600 text-xs mt-1"><i className="ri-gift-line mr-1"></i>Este plan será gratuito</p>
            )}
          </div>

          {/* Limits */}
          <div>
            <label className="block text-slate-600 text-xs font-semibold mb-3 uppercase tracking-wider">Límites del plan</label>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-4 h-4 flex items-center justify-center text-slate-400"><i className="ri-user-line text-xs"></i></span>
                  <p className="text-slate-500 text-xs font-medium">Usuarios</p>
                </div>
                <input
                  type="number"
                  min={1}
                  value={form.max_users}
                  onChange={(e) => set('max_users', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm bg-white border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {Number(form.max_users) >= 999 && <p className="text-[10px] text-blue-600 mt-1">≥ 999 = Ilimitado</p>}
              </div>
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-4 h-4 flex items-center justify-center text-slate-400"><i className="ri-file-text-line text-xs"></i></span>
                  <p className="text-slate-500 text-xs font-medium">Contratos</p>
                </div>
                <input
                  type="number"
                  min={1}
                  value={form.max_contracts}
                  onChange={(e) => set('max_contracts', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm bg-white border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {Number(form.max_contracts) >= 9999 && <p className="text-[10px] text-blue-600 mt-1">≥ 9999 = Ilimitado</p>}
              </div>
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-4 h-4 flex items-center justify-center text-slate-400"><i className="ri-hard-drive-line text-xs"></i></span>
                  <p className="text-slate-500 text-xs font-medium">Storage (MB)</p>
                </div>
                <input
                  type="number"
                  min={128}
                  step={128}
                  value={form.storage_mb}
                  onChange={(e) => set('storage_mb', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm bg-white border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-[10px] text-slate-400 mt-1">= {storageLabel(Number(form.storage_mb))}</p>
              </div>
            </div>
          </div>

          {/* Color */}
          <div>
            <label className="block text-slate-600 text-xs font-semibold mb-2 uppercase tracking-wider">Color del plan</label>
            <div className="flex items-center gap-2 flex-wrap">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set('color', c)}
                  className={`w-7 h-7 rounded-full border-2 cursor-pointer transition-transform hover:scale-110 ${form.color === c ? 'border-slate-900 scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <input
                type="color"
                value={form.color}
                onChange={(e) => set('color', e.target.value)}
                className="w-7 h-7 rounded-full border border-slate-200 cursor-pointer"
              />
              <span className="text-slate-400 text-xs font-mono">{form.color}</span>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
            <div>
              <p className="text-slate-700 text-sm font-medium">Plan activo</p>
              <p className="text-slate-400 text-xs">Los tenants pueden suscribirse a este plan</p>
            </div>
            <button
              type="button"
              onClick={() => set('is_active', !form.is_active)}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${form.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.is_active ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="plan-form"
            onClick={handleSubmit as unknown as React.MouseEventHandler<HTMLButtonElement>}
            disabled={saving}
            className="px-5 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
